import { useMemo, useState } from 'react';
import styles from './CompetitionPanel.module.css';
import drawer from './AnalysisDrawer.module.css';
import { LineChart, type Series } from '@/components/charts/LineChart';
import { Popover } from '@/components/ui/Popover';
import { Calendar } from '@/components/ui/Calendar';
import { OptionPicker } from '@/components/ui/OptionPicker';
import { BRAND_COLORS, useBrandSeries, useCompetitorBrands } from '@/hooks/useCompetition';
import { SkuCompetitionTable } from './SkuCompetitionTable';
import type { DateRange, WindowToken } from '@/lib/dateRange';

const METRICS = [
  { key: 'wt_osa', label: 'Wt. OSA%', available: true },
  { key: 'listing', label: 'DS Listing %', available: true },
  {
    key: 'osa_listed', label: 'Wt. OSA% in LS', available: false,
    reason: 'OSA-in-listed-stores needs the per-store listing atoms, which are not carried at the brand-day grain this chart reads.',
  },
  {
    key: 'assortment', label: 'Assortment', available: false,
    reason: 'Assortment is a distinct-SKU count, not a daily series on this resource.',
  },
] as const;

const RANGES: Array<{ token: WindowToken; label: string }> = [
  { token: 'last_30_days', label: '1M' },
  { token: 'last_90_days', label: '3M' },
];

/**
 * Competition Analysis — Figma 3727:43402.
 *
 * Competitor OSA and distribution are PUBLIC (observed from the dark-store
 * crawl), so the brand series is not tenant-filtered. Nothing partner-private
 * crosses brands: the tenant's own brands are flagged by the server purely to
 * badge them "(You)", and no advertising figure appears here at all.
 */
export function CompetitionPanel({
  customerName,
  category,
  city,
  range: pageRange,
}: {
  customerName: string;
  category: string | undefined;
  city: string | undefined;
  range: DateRange;
}) {
  const [range, setRange] = useState<DateRange>(pageRange);
  const [metric, setMetric] = useState<'wt_osa' | 'listing'>('wt_osa');
  // null = the user has not chosen yet, so the default below applies.
  const [chosen, setChosen] = useState<string[] | null>(null);
  // The comp has two competition views: brands compared on one chart
  // (3727:43402) and the ranked SKU table (4139:74371).
  const [view, setView] = useState<'brands' | 'skus'>('brands');

  const brands = useCompetitorBrands(customerName, category, range, true);

  /**
   * Opens on the comparison the user came for: their own brand against the
   * biggest rival. Derived rather than written into state by an effect —
   * setting state synchronously in an effect cascades a second render on every
   * brand load, and left the selection fighting the user's own clicks.
   */
  const picked = useMemo(() => {
    if (chosen !== null) return chosen;
    const rows = brands.data ?? [];
    const mine = rows.find((b) => b.mine)?.brand;
    const rival = rows.find((b) => !b.mine)?.brand;
    return [mine, rival].filter((b): b is string => Boolean(b));
  }, [chosen, brands.data]);

  const setPicked = (next: string[] | ((prev: string[]) => string[])): void => {
    setChosen(typeof next === 'function' ? next(picked) : next);
  };

  const q = useBrandSeries(customerName, picked, category, city, metric, range, true);

  const series: Series[] = useMemo(() => {
    if (!q.data) return [];
    return picked.map((b, i) => ({
      key: b,
      label: b,
      color: BRAND_COLORS[i % BRAND_COLORS.length],
      points: q.data.byBrand[b] ?? [],
      format: (v: number) => `${v.toFixed(1)}%`,
    }));
  }, [q.data, picked]);

  const top = (brands.data ?? []).slice(0, 4);
  const rest = (brands.data ?? []).slice(4);

  if (!category) {
    return (
      <p className={styles.state}>
        Pick a category first — competitors are only comparable within one.
      </p>
    );
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.chips}>
        {(['brands', 'skus'] as const).map((v) => (
          <button
            key={v}
            type="button"
            className={`${styles.radio} ${view === v ? styles.on : ''}`}
            onClick={() => { setView(v); }}
          >
            <span className={styles.dot}>{view === v && <span className={styles.dotOn} />}</span>
            {v === 'brands' ? 'Brands' : 'SKUs'}
          </button>
        ))}
      </div>

      {view === 'skus' ? (
        <SkuCompetitionTable customerName={customerName} category={category} city={city} range={range} />
      ) : (
      <>
      <div className={styles.group}>
        <span className={styles.groupLabel}>Select Brands</span>
        <div className={styles.chips}>
          {brands.isLoading && <span className={styles.groupLabel}>Loading brands…</span>}
          {top.map((b, i) => {
            const on = picked.includes(b.brand);
            const color = BRAND_COLORS[picked.indexOf(b.brand) % BRAND_COLORS.length] ?? BRAND_COLORS[i];
            return (
              <button
                key={b.brand}
                type="button"
                className={styles.brand}
                style={on ? { borderColor: color } : undefined}
                onClick={() => {
                  setPicked((p) => (p.includes(b.brand) ? p.filter((x) => x !== b.brand) : [...p, b.brand]));
                }}
              >
                <span className={styles.box} style={on ? { background: color, borderColor: color } : undefined}>
                  {on ? '✓' : ''}
                </span>
                {b.brand}
                {b.mine && <span className={styles.you}>(You)</span>}
              </button>
            );
          })}
          {rest.length > 0 && (
            <Popover
              trigger={({ toggle }) => (
                <button type="button" className={styles.more} onClick={toggle}>+{rest.length}</button>
              )}
            >
              {({ close }) => (
                <OptionPicker
                  options={rest.map((b) => ({ value: b.brand, label: b.brand, weight: b.sales }))}
                  value={undefined}
                  loading={false}
                  allLabel="Clear selection"
                  onSelect={(v) => {
                    setPicked((p) => (p.includes(v) ? p : [...p, v]));
                    close();
                  }}
                  onClear={() => { setPicked([]); close(); }}
                />
              )}
            </Popover>
          )}
        </div>
      </div>

      <div className={styles.group}>
        <span className={styles.groupLabel}>Select Metric</span>
        <div className={styles.chips}>
          {METRICS.map((m) => (
            <button
              key={m.key}
              type="button"
              disabled={!m.available}
              title={'reason' in m ? m.reason : m.label}
              className={`${styles.radio} ${metric === m.key ? styles.on : ''}`}
              onClick={() => { if (m.key === 'wt_osa' || m.key === 'listing') setMetric(m.key); }}
            >
              <span className={styles.dot}>{metric === m.key && <span className={styles.dotOn} />}</span>
              {m.label}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.card}>
        <div className={styles.cardHead}>
          <span className={drawer.ranges}>
            <Popover
              trigger={({ toggle }) => (
                <button type="button" className={`${drawer.range} ${range.token ? '' : drawer.active}`} onClick={toggle}>
                  🗓 Custom
                </button>
              )}
            >
              {({ close }) => (
                <Calendar value={range} onApply={(n) => { setRange(n); close(); }} onCancel={close} />
              )}
            </Popover>
            {RANGES.map((r) => (
              <button
                key={r.token}
                type="button"
                className={`${drawer.range} ${range.token === r.token ? drawer.active : ''}`}
                onClick={() => { setRange({ token: r.token }); }}
              >
                {r.label}
              </button>
            ))}
          </span>
        </div>

        {q.isLoading && <p className={styles.state}>Loading brand series…</p>}
        {q.isError && <p className={styles.state}>{q.error.message}</p>}
        {!q.isLoading && !q.isError && picked.length === 0 && (
          <p className={styles.state}>Select at least one brand.</p>
        )}
        {!q.isLoading && !q.isError && q.data && picked.length > 0 && (
          <LineChart labels={q.data.labels} series={series} yMax={100} height={360} />
        )}
        <p className={styles.note}>
          Competitor availability is public crawl data. Partner figures (actual sales, PSL) are never
          shown for a brand you are not aligned to.
        </p>
      </div>
      </>
      )}
    </div>
  );
}
